// src/features/files/components/FilePreviewTooltip.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Tooltip,
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';

// File utilities
import { supportsPreview } from '../utils/supportsPreview';
import { getFileIcon } from '../utils/getFileIcon';
import { formatFileSize } from '../utils/formatFileSize';
import { getFileTypeColor, getFileIconColor } from '../utils/getFileTypeColor';
import { getFileTypeLabel } from '../utils/getFileTypeLabel';
import { useFileUrl } from '../hooks/useFileUrl';

// Mounted only while the tooltip is open, so hovering is what fetches the link
function TooltipImage({ file, previewSize, onError }) {
  const { url } = useFileUrl(file.file_id, { size: previewSize });
  if (!url) return <CircularProgress size={24} />;
  return (
    <Box
      component="img"
      src={url}
      alt={file.file_name}
      onError={onError}
      sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
    />
  );
}

/**
 * FilePreviewTooltip - Wraps children with a tooltip that shows an expanded file preview
 * 
 * @param {Object} file - File object containing file_id, file_name, filetype, file_size, etc.
 * @param {React.ReactNode} children - The component to wrap (usually a small icon/thumbnail)
 * @param {string} [previewSize='256x256'] - Size of the preview image in tooltip
 */
const FilePreviewTooltip = ({
  file,
  children,
  previewSize = '256x256'
}) => {
  const { t } = useTranslation(['files']);
  const [imageError, setImageError] = useState(false);

  if (!file) {
    return children;
  }

  const isPreviewable = supportsPreview(file.filetype) && !imageError;

  const tooltipContent = (
    <Paper 
      elevation={8} 
      sx={{ 
        p: 2, 
        maxWidth: 320,
        bgcolor: 'background.paper',
      }}
    >
      <Stack spacing={1.5}>
        {/* Preview Image or Icon */}
        <Box
          sx={{
            width: '100%',
            height: 256,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.100',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          {isPreviewable ? (
            <TooltipImage file={file} previewSize={previewSize} onError={() => setImageError(true)} />
          ) : (
            <Box
              sx={{
                fontSize: 80,
                color: getFileIconColor(file.filetype),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {getFileIcon(file.filetype)}
            </Box>
          )}
        </Box>

        {/* File Name */}
        <Typography 
          variant="subtitle2" 
          sx={{ 
            fontWeight: 600,
            wordBreak: 'break-word',
          }}
        >
          {file.file_name}
        </Typography>

        {/* File Details */}
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {file.file_size && (
            <Chip 
              label={formatFileSize(file.file_size)} 
              size="small"
              variant="outlined"
            />
          )}
          {file.filetype && (
            <Chip 
              label={getFileTypeLabel(file)} 
              size="small"
              variant="outlined"
              color={getFileTypeColor(file.filetype)}
            />
          )}
        </Stack>

        {/* Attachment Label */}
        {file.attachment_label && (
          <Typography variant="body2" color="text.secondary">
            <strong>{t('files:file_preview.label')}:</strong> {file.attachment_label}
          </Typography>
        )}

        {/* Attached Info */}
        {(file.attached_at || file.attached_by) && (
          <Stack spacing={0.5}>
            {file.attached_at && (
              <Typography variant="caption" color="text.secondary">
                {t('files:file_preview.attached')} {format(parseISO(file.attached_at), 'PPp')}
              </Typography>
            )}
            {file.attached_by?.name && (
              <Typography variant="caption" color="text.secondary">
                {t('files:file_preview.by')} {file.attached_by.name}
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    </Paper>
  );

  return (
    <Tooltip
      title={tooltipContent}
      placement="top"
      arrow
      enterDelay={300}
      leaveDelay={200}
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -8],
              },
            },
          ],
        },
      }}
    >
      {children}
    </Tooltip>
  );
};

export default FilePreviewTooltip;